/**
 * Sustainability & ESG Services page content (/sustainability-esg-services).
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Sustainability & ESG Services (Main Page) — SEO Optimized 2026" requirement
 * document (MBA_MERN_Solutions/Sustainability & ESG Services.docx). Do not
 * reword, shorten or extend this copy — update the document first.
 *
 * NOTE the document also carries a per-service SEO Title and Meta Description
 * for each of the four services. Those are page metadata for future dedicated
 * sub-pages, not body copy, so they are NOT rendered here — only this page's
 * own title and description are used, from `sustainabilitySeo` below.
 *
 * `listLabel` and `benefitsLabel` are per service on purpose: the document
 * introduces each list with its own wording ("Our EcoVadis Consulting
 * Includes:", "Our ESG Consulting Services:", "Our GRI Reporting Support
 * Includes:", "Our SA8000 Consultancy Includes:"). Not interchangeable.
 *
 * `logoKey` values resolve against src/data/certificationLogos.js — all four
 * services have a real mark. `image` paths point at the illustrations already
 * in public/assets/certification-process. No new artwork was introduced.
 */

export const sustainabilitySeo = {
  title:
    "Sustainability & ESG Consulting Services | EcoVadis, GRI & SA8000 Consultancy | MBA Solutions",
  description:
    "MBA Solutions provides sustainability and ESG consulting services including EcoVadis preparation, ESG reporting, GRI standards and SA8000 compliance support for organizations.",
  slug: "/sustainability-esg-services",
};

export const heroContent = {
  h1: "Building Sustainable Businesses Through ESG & Responsible Practices",
  subtitle: "Helping Organizations Meet Global Sustainability Expectations",
  paragraphs: [
    "Sustainability is becoming a critical requirement for businesses, investors, customers and global buyers.",
    "MBA Solutions helps organizations improve their environmental, social and governance performance through internationally recognized sustainability frameworks and responsible business practices.",
    "Our consultants support businesses in understanding sustainability requirements, improving performance, preparing assessments and building stronger ESG strategies.",
  ],
};

export const servicesContent = {
  title: "Our Sustainability & ESG Services",
  standards: [
    {
      id: "ecovadis",
      logoKey: "ecovadis",
      title: "1. EcoVadis Consulting",
      headline: "EcoVadis Consulting – Improve Your Sustainability Rating",
      paragraphs: [
        "EcoVadis is one of the world’s leading sustainability assessment platforms used by global companies to evaluate supplier sustainability performance.",
        "MBA Solutions helps organizations prepare for EcoVadis assessments by improving sustainability practices, documentation and evidence management.",
        "We support businesses in building stronger sustainability systems aligned with global expectations.",
      ],
      listLabel: "Our EcoVadis Consulting Includes:",
      items: [
        "EcoVadis readiness assessment",
        "Current sustainability performance review",
        "Documentation improvement",
        "Policy development support",
        "Evidence preparation",
        "Improvement roadmap",
        "Assessment preparation",
      ],
      benefitsLabel: "Benefits of EcoVadis Support:",
      benefits: [
        "Improve sustainability score",
        "Meet buyer sustainability requirements",
        "Strengthen supplier credibility",
        "Improve ESG performance",
        "Increase business opportunities",
      ],
    },
    {
      id: "esg-consulting",
      logoKey: "esg",
      title: "2. ESG Consulting",
      headline: "ESG Consulting – Creating Responsible & Sustainable Businesses",
      paragraphs: [
        "Environmental, Social and Governance (ESG) practices help organizations measure and improve their impact on people, environment and business operations.",
        "MBA Solutions supports organizations in developing ESG strategies that align with international expectations and stakeholder requirements.",
      ],
      listLabel: "Our ESG Consulting Services:",
      items: [
        "ESG assessment",
        "Sustainability strategy development",
        "Environmental performance improvement",
        "Social responsibility guidance",
        "Governance improvement",
        "ESG documentation support",
      ],
      benefitsLabel: "Benefits of ESG Consulting:",
      benefits: [
        "Better sustainability management",
        "Improved stakeholder trust",
        "Stronger brand reputation",
        "Better investor confidence",
        "Future-ready business practices",
      ],
    },
    {
      id: "gri-reporting",
      logoKey: "gri",
      title: "3. GRI Reporting",
      headline: "GRI Reporting – Transparent Sustainability Communication",
      paragraphs: [
        "The Global Reporting Initiative (GRI) provides one of the most widely used frameworks for sustainability reporting.",
        "MBA Solutions helps organizations collect, organize and present sustainability information through structured reporting practices.",
      ],
      listLabel: "Our GRI Reporting Support Includes:",
      items: [
        "Sustainability reporting assessment",
        "Data collection guidance",
        "Materiality assessment support",
        "Report structure development",
        "Sustainability KPI identification",
        "Reporting improvement",
      ],
      benefitsLabel: "Benefits of GRI Reporting:",
      benefits: [
        "Transparent sustainability communication",
        "Improved stakeholder confidence",
        "Better ESG visibility",
        "Structured sustainability measurement",
        "Supports responsible business growth",
      ],
    },
    {
      id: "sa8000",
      logoKey: "sa8000",
      title: "4. SA8000 Certification",
      headline:
        "SA8000 Certification – Supporting Ethical & Responsible Workplaces",
      paragraphs: [
        "SA8000 is an international social accountability standard focused on ethical workplace practices, employee rights and responsible business operations.",
        "MBA Solutions helps organizations implement systems that support fair labor practices and social responsibility requirements.",
      ],
      listLabel: "Our SA8000 Consultancy Includes:",
      items: [
        "Social compliance assessment",
        "Workplace practice review",
        "Policy development",
        "Employee awareness training",
        "Documentation support",
        "Certification preparation",
      ],
      benefitsLabel: "Benefits of SA8000 Certification:",
      benefits: [
        "Improved workplace practices",
        "Stronger employee protection",
        "Better ethical reputation",
        "Improved buyer confidence",
        "Responsible supply chain development",
      ],
    },
  ],
};

export const processContent = {
  title: "Sustainability Implementation Process",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      title: "Step 1: Sustainability Assessment",
      description:
        "Reviewing current environmental, social and governance practices.",
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      title: "Step 2: Gap Analysis",
      description:
        "Identifying improvement areas against sustainability requirements.",
    },
    {
      number: "3",
      image: "/assets/certification-process/implementation.webp",
      title: "Step 3: Strategy Development",
      description:
        "Creating practical improvement plans and sustainability goals.",
    },
    {
      number: "4",
      image: "/assets/certification-process/documentation.webp",
      title: "Step 4: Documentation & Reporting",
      description:
        "Developing required policies, records and sustainability documentation.",
    },
    {
      number: "5",
      image: "/assets/certification-process/certification-support.webp",
      title: "Step 5: Implementation Support",
      description: "Helping organizations apply sustainable practices.",
    },
    {
      number: "6",
      image: "/assets/certification-process/internal-audit.webp",
      title: "Step 6: Assessment & Improvement",
      description:
        "Preparing for ratings, audits and continuous improvement.",
    },
  ],
};

export const industriesContent = {
  title: "Industries We Support",
  intro: "MBA Solutions provides sustainability and ESG support for:",
  industries: [
    { icon: "textile", name: "Textile & Garments" },
    { icon: "manufacturing", name: "Manufacturing" },
    { icon: "food", name: "Food Industry" },
    { icon: "export", name: "Export Businesses" },
    { icon: "supplyChain", name: "Supply Chain Organizations" },
    { icon: "corporate", name: "Corporate Organizations" },
    { icon: "service", name: "Service Companies" },
  ],
};

export const whyChooseContent = {
  title: "Why Choose MBA Solutions?",
  reasons: [
    "Sustainability & Compliance Expertise",
    "Global Standards Knowledge",
    "Practical Business Solutions",
    "ESG Improvement Support",
    "Complete Consultancy Approach",
  ],
};

export const finalCtaContent = {
  title: "Build A More Sustainable Future With MBA Solutions",
  paragraph:
    "Improve your ESG performance, strengthen compliance and meet global sustainability expectations.",
  cta: { label: "Get Sustainability Consultation Today", to: "/contact" },
};
